import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Leaflet
import { MapContainer, Marker, Popup, TileLayer, Tooltip } from 'react-leaflet';
import { LatLngExpression, Map } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Utils
import {
  createClusterCustomMarker,
  createDivMarkerIcon,
  createPinIcon,
  MapPlaceholder,
  MarkerClusterGroup,
} from '../../../../utils/leaflet-map-utilities/leaflet-map-utilities';

// Hooks & services
import { getAllStations } from '../../../../services/station.service';

// Constants
import { ReadersStationMapView } from '../../../../constants/Constants';

// Types
import { getReaderUrl, Reader } from '../../../../types/Reader';
import { StationResponse } from '../../../../types/Station';

// Common components
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import GeoLocationSearch from './GeoLocationSearch';
import ReadersByStationIdModal from './ReadersByStationIdModal';

export type Place = {
  id: string;
  picture: string;
  title: string;
  description: string;
  seeMoreLink: string;
  position: LatLngExpression;
};

export interface SelectedStation {
  name: string;
  readerCount: number;
  id: string;
}

const defaultPosition: LatLngExpression = [-0.002988, 57.735438];

const ReaderMapView = ({ isVisible = true }: { isVisible?: boolean }): React.JSX.Element => {
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [stations, setStations] = useState<StationResponse[]>([]);
  const [selectedStation, setSelectedStation] = useState<SelectedStation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [readers, setReaders] = useState<Reader[]>([]);

  const mapRef = useRef<Map | null>(null);

  const fetchStationsData = useCallback(async () => {
    const filters = {
      'filters[0].fieldName': 'readerCount',
      'filters[0].fieldValue': 'greaterThan',
      'filters[0].filterType': 0,
      take: 1000000,
      fields: 'id,name,location,readerCount,iataCode',
    };

    const response = await getAllStations(filters);
    return response.data.results;
  }, []);

  useEffect(() => {
    setStatus('loading');
    fetchStationsData()
      .then((data) => {
        setStations(data);
        setStatus('success');
      })
      .catch((e) => {
        console.error('Error: ', e);
        setStatus('error');
        setErrorMessage(e.message);
      });
  }, [fetchStationsData]);

  useEffect(() => {
    if (mapRef.current && isVisible) {
      mapRef.current.invalidateSize();
    }
  }, [isVisible]);

  if (status === 'loading') {
    return <div>{ReadersStationMapView.LoadingMap}</div>;
  }

  if (status === 'error') {
    return (
      <div>
        {ReadersStationMapView.ErrorMapData} {errorMessage}
      </div>
    );
  }

  const resizeMap = (mapRef: RefObject<Map>) => {
    const container = document.getElementById('map-container');
    if (container) {
      const resizeObserver = new ResizeObserver(() => mapRef.current?.invalidateSize());
      resizeObserver.observe(container);
    }
  };

  const handleStationMarkerClick = (station: SelectedStation) => {
    setSelectedStation(station);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedStation(null);
    setReaders([]);
  };

  const handleFetchReaders = (readers: Reader[]) => setReaders(readers);

  return (
    <div style={{ height: '80vh', display: 'flex' }}>
      <PanelGroup autoSaveId='example' direction='horizontal' style={{ border: '1px solid lightgrey' }}>
        <Panel minSize={15} defaultSize={50}>
          <MapContainer
            center={defaultPosition}
            ref={mapRef}
            zoom={2.2}
            placeholder={<MapPlaceholder />}
            style={{
              width: '100%',
              height: '100%',
            }}
            id='map-container'
            whenReady={() => resizeMap(mapRef)}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              maxNativeZoom={18}
              maxZoom={20}
            />
            <GeoLocationSearch />

            {readers.length > 0 &&
              readers
                .filter((x) => x.lockedLocation != null || x.location != null)
                .map((x, index) => (
                  <Marker
                    key={index}
                    position={[x.lockedLocation?.latitude ?? x.location!.latitude, x.lockedLocation?.longitude ?? x.location!.longitude]}
                    icon={createPinIcon()}
                    alt='Reader'
                  >
                    <Popup>
                      <Link to={getReaderUrl(x)}>
                        {x.imei}
                        <br />
                        {x.name}
                      </Link>
                    </Popup>
                  </Marker>
                ))}

            <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterCustomMarker} showCoverageOnHover={false} spiderfyOnMaxZoom={true}>
              {stations && stations.length > 0 ? (
                stations
                  .filter((s) => s.location != null)
                  .map((s, index) => (
                    <Marker
                      key={index}
                      position={[s.location!.latitude, s.location!.longitude]}
                      title={s.readerCount.toString()}
                      icon={createDivMarkerIcon({
                        className: 'reader-airport-icon ' + (selectedStation?.id == s.id && 'selected'),
                        html: `<span class="text-thin">${s.iataCode ?? ''}</span>
                            <span data-testid="circular-marker">${s.readerCount}</span>`,
                      })}
                      alt='Number of readers at this station'
                      eventHandlers={{
                        click: () => handleStationMarkerClick(s),
                      }}
                    >
                      <Tooltip>{`Station: ${s.name}`}</Tooltip>
                    </Marker>
                  ))
              ) : (
                <div>{ReadersStationMapView.NoDataAvailable}</div>
              )}
            </MarkerClusterGroup>
          </MapContainer>
        </Panel>
        <PanelResizeHandle />
        <Panel hidden={!selectedStation || !isModalOpen} defaultSize={50} style={{ borderLeft: '1px solid lightgrey', overflowY: 'auto' }}>
          {selectedStation && isModalOpen && (
            <ReadersByStationIdModal isOpen={isModalOpen} onFetchReaders={handleFetchReaders} onClose={handleModalClose} station={selectedStation} />
          )}
        </Panel>
      </PanelGroup>
    </div>
  );
};

export default ReaderMapView;
